import { reasonSchema, type SubscriptionOverview } from '@fruuit/shared'
import { Button, Callout, Dialog, DialogCancel, Select, Textarea } from '@fruuit/ui'
import { type FormEvent, useId, useState } from 'react'
import { ApiError, errorMessage } from '../../api/client'
import { schemaMessage } from './ReasonDialog'

export interface RestrictionInput {
  user_id: string
  engagement_id: string
  reason: string
}

/**
 * Narrow one user's access to one subscribed case (D-04). The org keeps its subscription;
 * the restricted user stops seeing the case on their next request.
 */
export function RestrictionDialog({
  overview,
  onClose,
  onConfirm,
}: {
  overview: SubscriptionOverview
  onClose: () => void
  onConfirm: (input: RestrictionInput) => Promise<unknown>
}) {
  const formId = useId()
  const [userId, setUserId] = useState('')
  const [engagementId, setEngagementId] = useState('')
  const [reason, setReason] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [formError, setFormError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const members = overview.members.filter(
    (m) => m.role === 'investor' || m.role === 'investor_admin',
  )
  const subscribed = overview.engagements.filter((e) => e.subscription !== null)

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (busy) return
    const next: Record<string, string> = {}
    if (!userId) next.user_id = 'Choose a user.'
    if (!engagementId) next.engagement_id = 'Choose a subscribed case.'
    const reasonError = schemaMessage(reasonSchema, reason)
    if (reasonError) next.reason = reasonError
    setErrors(next)
    setFormError(null)
    if (Object.keys(next).length > 0) return
    setBusy(true)
    try {
      await onConfirm({ user_id: userId, engagement_id: engagementId, reason: reason.trim() })
    } catch (error) {
      if (error instanceof ApiError && Object.keys(error.fieldErrors).length > 0) {
        setErrors(error.fieldErrors)
      } else {
        setFormError(errorMessage(error))
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title="Restrict a user"
      description={`${overview.org.name} keeps its subscription. The chosen user stops seeing the case on their next request.`}
      size="sm"
      footer={
        <>
          <DialogCancel />
          <Button type="submit" form={formId} loading={busy}>
            Add restriction
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={submit} noValidate className="flex flex-col gap-4">
        <Select
          label="User"
          required
          placeholder="Choose a user"
          options={members.map((m) => ({ value: m.user_id, label: `${m.name} (${m.email})` }))}
          value={userId}
          onChange={(event) => setUserId(event.target.value)}
          error={errors.user_id}
        />
        <Select
          label="Case"
          required
          placeholder="Choose a subscribed case"
          options={subscribed.map((e) => ({
            value: e.engagement_id,
            label: `${e.title} (${e.issuer_name})`,
          }))}
          value={engagementId}
          onChange={(event) => setEngagementId(event.target.value)}
          error={errors.engagement_id}
        />
        <Textarea
          label="Reason"
          required
          hint="At least 8 characters. Recorded in the audit trail."
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          error={errors.reason}
        />
        {formError ? (
          <Callout tone="danger" role="alert">
            {formError}
          </Callout>
        ) : null}
      </form>
    </Dialog>
  )
}
